import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { ProtectedRoute } from '@/components/common/ProtectedRoute';
// Public pages
import { HomePage } from '@/pages/HomePage';
import { CategoryPage } from '@/pages/CategoryPage';
import { ProductDetailPage } from '@/pages/ProductDetailPage';
import { SearchPage } from '@/pages/SearchPage';
import { CartPage } from '@/pages/CartPage';
import { AboutPage } from '@/pages/AboutPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

// Auth pages
import { LoginPage } from '@/pages/auth/LoginPage';
import { RegisterPage } from '@/pages/auth/RegisterPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { VerifyEmailPage } from '@/pages/auth/VerifyEmailPage';

// User pages (authenticated)
import { CheckoutPage } from '@/pages/CheckoutPage';
import { OrderConfirmationPage } from '@/pages/OrderConfirmationPage';
import { OrdersPage } from '@/pages/OrdersPage';
import { OrderDetailPage } from '@/pages/OrderDetailPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { PrescriptionPage } from '@/pages/PrescriptionPage';
import { WishlistPage } from '@/pages/WishlistPage';

// Admin pages
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import { AdminProducts } from '@/pages/admin/AdminProducts';
import { AdminOrders } from '@/pages/admin/AdminOrders';
import { AdminInventory } from '@/pages/admin/AdminInventory';

// Pharmacist
import { PharmacistDashboard } from '@/pages/pharmacist/PharmacistDashboard';

// New features
import { DrugCheckerPage } from '@/pages/DrugCheckerPage';
import { SymptomSearchPage } from '@/pages/SymptomSearchPage';
import { ExpenseVaultPage } from '@/pages/ExpenseVaultPage';
import { PickupQueuePage } from '@/pages/PickupQueuePage';
import { FamilyCabinetPage } from '@/pages/FamilyCabinetPage';
import { ReferralPage } from '@/pages/ReferralPage';
import { SubscriptionsPage } from '@/pages/SubscriptionsPage';
import { OwnerAnalyticsPage } from '@/pages/OwnerAnalyticsPage';
import { WhatsAppSettingsPage } from '@/pages/WhatsAppSettingsPage';

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Auth routes — no layout */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />

        {/* Main layout */}
        <Route element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="categories/:slug" element={<CategoryPage />} />
          <Route path="products/:slug" element={<ProductDetailPage />} />
          <Route path="search" element={<SearchPage />} />
          <Route path="cart" element={<CartPage />} />
          <Route path="about" element={<AboutPage />} />

          {/* Authenticated user routes */}
          <Route path="checkout" element={<ProtectedRoute><CheckoutPage /></ProtectedRoute>} />
          <Route path="order-confirmation/:orderNumber" element={<ProtectedRoute><OrderConfirmationPage /></ProtectedRoute>} />
          <Route path="orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
          <Route path="orders/:id" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
          <Route path="prescriptions" element={<ProtectedRoute><PrescriptionPage /></ProtectedRoute>} />
          <Route path="wishlist" element={<ProtectedRoute><WishlistPage /></ProtectedRoute>} />
          <Route path="profile/*" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />

          {/* Tools — public */}
          <Route path="drug-checker" element={<DrugCheckerPage />} />
          <Route path="symptoms" element={<SymptomSearchPage />} />

          {/* Authenticated user tools */}
          <Route path="expenses" element={<ProtectedRoute><ExpenseVaultPage /></ProtectedRoute>} />
          <Route path="family" element={<ProtectedRoute><FamilyCabinetPage /></ProtectedRoute>} />
          <Route path="referral" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
          <Route path="subscriptions" element={<ProtectedRoute><SubscriptionsPage /></ProtectedRoute>} />
          <Route path="whatsapp-settings" element={<ProtectedRoute><WhatsAppSettingsPage /></ProtectedRoute>} />

          {/* Admin only */}
          <Route path="owner-analytics" element={<ProtectedRoute roles={['ADMIN']}><OwnerAnalyticsPage /></ProtectedRoute>} />

          {/* Pharmacist / Admin */}
          <Route
            path="pharmacist"
            element={<ProtectedRoute roles={['PHARMACIST', 'ADMIN']}><PharmacistDashboard /></ProtectedRoute>}
          />
          <Route
            path="pickup-queue"
            element={<ProtectedRoute roles={['PHARMACIST', 'ADMIN']}><PickupQueuePage /></ProtectedRoute>}
          />

          <Route path="404" element={<NotFoundPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Admin layout */}
        <Route
          path="admin"
          element={<ProtectedRoute roles={['ADMIN']}><AdminLayout /></ProtectedRoute>}
        >
          <Route index element={<AdminDashboard />} />
          <Route path="products" element={<AdminProducts />} />
          <Route path="orders" element={<AdminOrders />} />
          <Route path="inventory" element={<AdminInventory />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
